import { LitElement, html, css } from "lit";
import { customElement, property, state } from "lit/decorators.js";

/**
 * The severity vocabulary of the control-plane policy linter (`SC-F529`):
 * `error` blocks save, `blocking-warning` blocks publish but allows save, and
 * `warning` is informational. Both bn-field and bn-input colour a diagnostic by
 * it, so a warning never looks like a rejection.
 */
export type BnDiagnosticSeverity = "error" | "blocking-warning" | "warning";

/**
 * bn-field is the labeled form-row wrapper: a label (with an optional required
 * asterisk), a slotted control (bn-input, bn-code, a native input, a select, …),
 * and one message row below it — a hint, or a diagnostic that replaces it. It
 * carries no input state itself.
 *
 * The message row is where a linter finding lands. `error` is the message and
 * `rule` is the `SC-F529` rule id, rendered as a monospace chip in front of it,
 * so the user reads *which* rule fired and not just that something is wrong. For
 * a finding that needs more than text — a documentation link, a one-click fix —
 * slot it into the `error` slot instead; the slot wins over the `error`
 * property, and the rule chip and severity colour still apply.
 *
 * Properties:
 * - `label`    — the visible label text
 * - `hint`     — help text, shown only while no diagnostic is present
 * - `error`    — the diagnostic message (fallback content of the `error` slot)
 * - `rule`     — linter rule id, e.g. `L04`; rendered as a chip when set
 * - `severity` — `error` (default) | `blocking-warning` | `warning`
 * - `required` — renders the asterisk after the label
 *
 * Slots: the default slot takes the control; the `error` slot takes rich
 * diagnostic content.
 *
 * Events: none.
 *
 * Accessibility: the label lives in this shadow root, so `for=` cannot reach a
 * slotted control. bn-field therefore copies `label` onto each assigned element
 * as `aria-label` (it never overwrites an `aria-label` or `aria-labelledby` the
 * page set itself), and bn-input forwards that to its internal input. The
 * diagnostic row is `role="alert"` at `error` severity and `role="status"`
 * otherwise; for a diagnostic that must be *associated* with the control rather
 * than announced, use bn-input's own `diagnostic` property, which is wired with
 * `aria-describedby` inside bn-input's own root.
 *
 *   <bn-field label="Instance name" required hint="Lowercase, 63 characters max.">
 *     <bn-input .value=${name} @input=${(e) => (this.name = e.detail.value)}></bn-input>
 *   </bn-field>
 *
 *   <bn-field label="Source IP ranges" rule="L04" severity="blocking-warning">
 *     <bn-input disabled></bn-input>
 *     <span slot="error">Never matches in this build — see
 *       <a href="/docs/linter#L04">rule L04</a>.</span>
 *   </bn-field>
 */
@customElement("bn-field")
export class BnField extends LitElement {
  @property() label = "";
  @property() hint = "";
  @property() error = "";
  /** Linter rule id (`SC-F529`), shown as a chip in front of the message. */
  @property() rule = "";
  @property() severity: BnDiagnosticSeverity = "error";
  @property({ type: Boolean }) required = false;

  /** True once the `error` slot has non-whitespace content assigned to it. */
  @state() private hasSlottedError = false;

  /** The label text this element last wrote onto its slotted control. */
  private appliedLabel = "";

  static override styles = css`
    :host {
      display: block;
      margin-bottom: var(--bino-space-md);
    }
    label {
      display: block;
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-base);
      font-weight: 500;
      color: var(--bino-text-muted);
      margin-bottom: 0.375rem;
    }
    .req {
      color: var(--bino-error);
      margin-left: 2px;
    }
    .hint {
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      margin-top: 0.375rem;
    }
    .diag {
      display: flex;
      align-items: baseline;
      gap: var(--bino-space-xs);
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-sm);
      margin-top: 0.375rem;
    }
    /* The UA rule for [hidden] loses to .diag's display, so restate it. */
    .diag[hidden] {
      display: none;
    }
    .diag.error {
      color: var(--bino-error);
    }
    .diag.warning {
      color: var(--bino-warning);
    }
    .rule {
      flex: none;
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-xs);
      padding: 0 var(--bino-space-xs);
      border-radius: var(--bino-radius-sm);
      background: var(--bino-surface-inset);
    }
  `;

  /**
   * Copies `label` onto the slotted control as `aria-label`. Runs on every
   * update as well as on slotchange, so a label that changes after the control
   * was assigned still reaches it — but only overwrites a value this element
   * wrote, never one the page set.
   */
  private applyLabel() {
    const slot = this.renderRoot.querySelector<HTMLSlotElement>("slot:not([name])");
    if (!slot) return;
    for (const el of slot.assignedElements()) {
      if (el.hasAttribute("aria-labelledby")) continue;
      const current = el.getAttribute("aria-label");
      if (current === null || current === this.appliedLabel) {
        if (this.label) el.setAttribute("aria-label", this.label);
        else el.removeAttribute("aria-label");
      }
    }
    this.appliedLabel = this.label;
  }

  private onErrorSlotChange(e: Event) {
    this.hasSlottedError = (e.target as HTMLSlotElement)
      .assignedNodes({ flatten: true })
      .some((n) => n.nodeType !== Node.TEXT_NODE || (n.textContent ?? "").trim() !== "");
  }

  override updated() {
    this.applyLabel();
  }

  override render() {
    const shown = !!this.error || this.hasSlottedError;
    const tone = this.severity === "error" ? "error" : "warning";
    return html`
      ${this.label
        ? html`<label
            >${this.label}${this.required
              ? html`<span class="req" aria-hidden="true">*</span>`
              : ""}</label
          >`
        : ""}
      <slot @slotchange=${this.applyLabel}></slot>
      <div
        class="diag ${tone}"
        ?hidden=${!shown}
        role=${this.severity === "error" ? "alert" : "status"}
      >
        ${this.rule
          ? html`<code class="rule" title="Policy linter rule ${this.rule}">${this.rule}</code>`
          : ""}<slot name="error" @slotchange=${this.onErrorSlotChange}>${this.error}</slot>
      </div>
      ${!shown && this.hint ? html`<div class="hint">${this.hint}</div>` : ""}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bn-field": BnField;
  }
}
